/**
 * Compact status bar for conversation cache / background refresh states.
 */

import React from "react";
import { Button, Flex, Spin, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloudSyncOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { formatDistanceToNow } from "date-fns";
import { SYNC_VARIANT } from "../utils/conversationSyncStatus.js";

const { Text } = Typography;

function formatCachedAt(cachedAt) {
  if (!cachedAt) return null;
  try {
    return formatDistanceToNow(new Date(cachedAt), { addSuffix: true });
  } catch {
    return null;
  }
}

function StatusIcon({ variant }) {
  if (variant === SYNC_VARIANT.LOADING || variant === SYNC_VARIANT.REFRESHING) {
    return <Spin size="small" />;
  }
  if (variant === SYNC_VARIANT.UP_TO_DATE || variant === SYNC_VARIANT.UPDATED) {
    return <CheckCircleOutlined style={{ color: "#389e0d" }} />;
  }
  if (variant === SYNC_VARIANT.CACHED_STALE || variant === SYNC_VARIANT.ERROR) {
    return <ExclamationCircleOutlined style={{ color: "#d48806" }} />;
  }
  return <CloudSyncOutlined />;
}

const ConversationSyncStatusBar = ({
  syncStatus,
  cachedAt = null,
  onRetry,
  compact = false,
}) => {
  if (!syncStatus?.variant || !syncStatus?.message) {
    return null;
  }

  const savedLabel = formatCachedAt(cachedAt);
  const isPositive =
    syncStatus.variant === SYNC_VARIANT.UP_TO_DATE ||
    syncStatus.variant === SYNC_VARIANT.UPDATED;

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={8}
      wrap="wrap"
      style={{
        padding: compact ? "4px 8px" : "6px 10px",
        borderRadius: 8,
        background: isPositive ? "#f6ffed" : "#fafafa",
        border: `1px solid ${isPositive ? "#b7eb8f" : "#f0f0f0"}`,
      }}
      role="status"
      aria-live="polite"
    >
      <Flex align="center" gap={8} style={{ minWidth: 0 }}>
        <StatusIcon variant={syncStatus.variant} />
        <Flex vertical gap={0} style={{ minWidth: 0 }}>
          <Text style={{ fontSize: compact ? 12 : 13 }}>{syncStatus.message}</Text>
          {savedLabel &&
            (syncStatus.variant === SYNC_VARIANT.REFRESHING ||
              syncStatus.variant === SYNC_VARIANT.CACHED_STALE) && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Saved {savedLabel}
              </Text>
            )}
        </Flex>
      </Flex>

      {syncStatus.showRetry && onRetry && (
        <Button
          size="small"
          type="link"
          icon={<ReloadOutlined />}
          onClick={onRetry}
          style={{ paddingInline: 0 }}
        >
          Retry
        </Button>
      )}

      {syncStatus.variant === SYNC_VARIANT.REFRESHING && (
        <SyncOutlined spin style={{ color: "#8c8c8c", fontSize: 12 }} />
      )}
    </Flex>
  );
};

export default ConversationSyncStatusBar;
